import { IDocument } from '@/models/documentsSettings.model';
import { DocumentSourceEnum } from '@/models/documents.model';
import { showApiErrors } from '@/utils';
import { createSlice, isPending, isRejected, nanoid, PayloadAction } from '@reduxjs/toolkit';
import { createDocument, deleteDocument, getRequiredDocuments, updateDocument } from './actionCreators';
import { DOC_SETTINGS_SLICE_NAME, DocumentsSettingsState, initialState } from './models';

export const slice = createSlice({
  name: DOC_SETTINGS_SLICE_NAME,
  initialState,
  reducers: {
    resetState: () => initialState,

    addRequiredDocument: (state: DocumentsSettingsState, action: PayloadAction<IDocument>) => {
      const { payload } = action;
      if (!state.documents) return;
      state.documents = {
        ...state.documents,
        [payload.source_type]: [
          {
            ...payload,
            createId: nanoid(),
          },
          ...state.documents[payload.source_type],
        ],
      };
    },

    removeRequiredDocument: (
      state: DocumentsSettingsState,
      action: PayloadAction<{
        id: string;
        sourceType: DocumentSourceEnum;
      }>,
    ) => {
      const { payload } = action;
      if (!state.documents) return;
      state.documents = {
        ...state.documents,
        [payload.sourceType]: state.documents[payload.sourceType].filter(({ createId }) => createId !== payload.id),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRequiredDocuments.fulfilled, (state: DocumentsSettingsState, action) => {
        const { payload } = action;
        state.documents = payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createDocument.fulfilled, (state: DocumentsSettingsState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        const prevState = state.documents?.[payload.source_type] ?? [];
        if (!state.documents) return;
        state.documents = {
          ...state.documents,
          [action.payload.source_type]: prevState.map((item) => (item.createId === payload.createId ? payload : item)),
        };
      })
      .addCase(deleteDocument.fulfilled, (state: DocumentsSettingsState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        if (!state.documents) return;
        state.documents = {
          ...state.documents,
          [payload.source_type]: state.documents[payload.source_type].filter((item) => item.id !== payload.id),
        };
      })
      .addCase(updateDocument.fulfilled, (state: DocumentsSettingsState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        const prevState = state.documents?.[payload.source_type] ?? [];
        if (!state.documents) return;
        state.documents = {
          ...state.documents,
          [payload.source_type]: prevState.map((item) => (item.id === payload.id ? payload : item)),
        };
      })
      .addMatcher(
        isPending(getRequiredDocuments, createDocument, deleteDocument, updateDocument),
        (state: DocumentsSettingsState) => {
          state.isLoading = true;
          state.error = null;
        },
      )
      .addMatcher(
        isRejected(getRequiredDocuments, createDocument, deleteDocument, updateDocument),
        (state: DocumentsSettingsState, action) => {
          const { error } = action;
          state.isLoading = false;
          state.error = error;

          showApiErrors(error);
        },
      );
  },
});

export default slice.reducer;

export const { resetState, addRequiredDocument, removeRequiredDocument } = slice.actions;
